import { useState, type ReactNode } from "react";
import { Download, Maximize2, Pin } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversationContext } from "~/pages/manifold-nextgen/components/conversations/ConversationContext";
import { PinArtifactDialog } from "~/pages/manifold-nextgen/components/projects/PinArtifactDialog";
import { usePinTarget } from "~/pages/manifold-nextgen/components/projects/usePinTarget";
import type { ArtifactPreview } from "~/services/artifacts";
import { DownloadDialog } from "./DownloadDialog";

interface ArtifactToolbarProps {
  preview: ArtifactPreview;
  // Optional expand-to-modal action — wired by the shell when the
  // renderer supplies an `expandedContent` slot.
  onExpand?: () => void;
}

// Icon-only action row shown on the right side of every artifact card
// header. Tooltips provide labels. Layout:
//   [📌]? [↓] [⤢]?
// Download opens a modal that handles workbench/format selection and
// progress — no inline caret menu. Expand only renders when the artifact
// shell was given an expanded view. Pin only renders inside a project
// session, to someone who can curate that project's overview.
export const ArtifactToolbar = ({ preview, onExpand }: ArtifactToolbarProps) => {
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  // Share-token viewers don't have a conversationId, and the download
  // endpoint isn't token-scoped — hide the action rather than render a
  // button that 404s. Expand is local-only, so it stays.
  const { conversationId } = useConversationContext();
  const showActions = conversationId !== null;
  // Null outside a project route, or for a member who cannot curate.
  const pinTarget = usePinTarget();
  const canPin = showActions && pinTarget !== null && preview.artifact_name !== null;

  return (
    <>
      <div className="flex items-center gap-0.5">
        {canPin && (
          <IconAction
            label="Pin to project"
            icon={<Pin className="size-3.5" />}
            onClick={() => setPinOpen(true)}
          />
        )}
        {showActions && (
          <IconAction
            label="Download"
            icon={<Download className="size-3.5" />}
            onClick={() => setDownloadOpen(true)}
          />
        )}
        {onExpand && (
          <IconAction label="Expand" icon={<Maximize2 className="size-3.5" />} onClick={onExpand} />
        )}
      </div>
      {showActions && (
        <DownloadDialog
          open={downloadOpen}
          onClose={() => setDownloadOpen(false)}
          artifactId={preview.artifact_id}
          artifactType={preview.artifact_type}
        />
      )}
      {canPin && pinOpen && conversationId && preview.artifact_name && (
        <PinArtifactDialog
          open={pinOpen}
          onClose={() => setPinOpen(false)}
          projectId={pinTarget.projectId}
          conversationId={conversationId}
          artifactName={preview.artifact_name}
          defaultDisplayName={preview.summary ?? preview.artifact_name}
        />
      )}
    </>
  );
};

interface IconActionProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const IconAction = ({ label, icon, onClick, disabled }: IconActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
        className="h-7 w-7 p-0 text-muted-foreground"
      >
        {icon}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);
